import { CheckoutForm } from ".";
import { useFormContext } from "react-hook-form";
import { useLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { Icon } from "@circle-react/components/shared/Icon";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Button } from "@circle-react-uikit/Button";
import { Loader } from "@circle-react-uikit/Loader";
import { usePayment } from "../../hooks/usePayment";
import { CheckoutDescription } from "../CheckoutDescription";
import { PaywallCustomization } from "../CheckoutDescription/PaywallCustomization";
import { Column } from "../components/Column";
import { CheckoutFormAccountInfo } from "./CheckoutFormAccountInfo";
import { CheckoutFormSection } from "./CheckoutFormSection";
import { CheckoutFormTOS } from "./CheckoutFormTOS";
import { CheckoutFormTaxIdField } from "./CheckoutFormTaxIdField";
import { PaymentMethodSelectorForm } from "./PaymentMethodSelectorForm";
import { Elements } from "./StripeElements";

interface RootProps {
  className?: string;
}

export const Root = ({ className }: RootProps) => {
  const formMethods = useFormContext();
  const { handleSubmit } = formMethods;
  const {
    isProcessingPayment,
    authProviderProperties,
    checkoutPreviewContext,
  } = usePaywallCheckoutContext();

  const { onSubmit, payButtonLabel, stripe, isSubmitDisabled } = usePayment();
  const isLgOrLargerScreen = useLgScreenMediaQuery();

  if (authProviderProperties?.is_sso_authentication_required) {
    return null;
  }

  return (
    <Column
      className={classNames(
        "flex h-auto w-full flex-col items-center lg:items-start",
        className,
        {
          "pb-6": !!checkoutPreviewContext,
        },
      )}
      dataTestId="paywall-checkout-right-column"
    >
      <CheckoutDescription.CommunityHeader className="hidden lg:invisible lg:block" />
      {isProcessingPayment && <Loader variant="fullscreen" />}
      <form
        className="flex flex-col gap-y-6"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="checkout-form"
      >
        <CheckoutForm.Alerts className="hidden lg:block" shouldFocus />
        <CheckoutFormAccountInfo />
        <CheckoutFormSection className="gap-y-5">
          <PaymentMethodSelectorForm />
          <Elements />
          <CheckoutFormTaxIdField />
        </CheckoutFormSection>
        <CheckoutForm.Alerts className="block lg:hidden" />
        <CheckoutFormSection className="gap-y-5">
          <CheckoutFormTOS />
          <Button
            type="submit"
            extraLarge
            variant="circle"
            disabled={!stripe || isSubmitDisabled || isProcessingPayment}
            data-testid="checkout-form-submit-button"
            className="flex flex-row items-center justify-center gap-x-2"
          >
            <Icon size={20} type="20-lock" />
            {payButtonLabel}
          </Button>
        </CheckoutFormSection>
      </form>
      <PaywallCustomization shouldHide={isLgOrLargerScreen} />
    </Column>
  );
};
